import { useState } from "react";
import { Zap, LogOut, User, Pencil, Settings, Library, Menu } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { DinoMark } from "./DinoMark";
import { useAuth } from "@/hooks/useAuth";
import { useMyProfile } from "@/hooks/useMyProfile";
import { Avatar } from "./Avatar";
import { AuthDialog } from "./AuthDialog";
import { TopBarSearch } from "./TopBarSearch";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

export function TopBar() {
  const { user, signOut } = useAuth();
  const { data: profile } = useMyProfile();
  const [authOpen, setAuthOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="mx-auto flex h-12 max-w-grid items-center gap-4 px-4">
        {/* Mobile and tablet menu — below 1024px the bar can't fit five nav
            links beside search, so places live in a drawer and the search row
            breathes. */}
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              aria-label="Menu"
              className="-ml-1 inline-flex h-8 w-8 items-center justify-center rounded-[5px] text-text-bright hover:text-primary lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          {/* aria-describedby is explicitly cleared: the drawer is a nav list
              with no description, and Radix warns loudly when the prop is
              simply absent. */}
          <SheetContent
            side="left"
            aria-describedby={undefined}
            className="w-[260px] border-border bg-background p-0"
          >
            <SheetTitle className="px-4 pb-1 pt-4 text-[15px] font-black tracking-[-0.02em] text-text-dim">
              Menu
            </SheetTitle>
            <nav aria-label="Primary" className="flex flex-col">
              <MenuLink to="/" label="Browse" exact onNavigate={() => setMenuOpen(false)} />
              <MenuLink
                to="/collections"
                label="Collections"
                onNavigate={() => setMenuOpen(false)}
              />
              <MenuLink to="/calendar" label="Calendar" onNavigate={() => setMenuOpen(false)} />
              <MenuLink to="/play" label="Play" onNavigate={() => setMenuOpen(false)} />
              <MenuLink to="/night" label="Movie Night" onNavigate={() => setMenuOpen(false)} />
              <div className="mx-4 my-2 border-t border-border" />
              <MenuLink
                to="/watched"
                label="Rate titles"
                icon={<Zap className="h-3.5 w-3.5" />}
                onNavigate={() => setMenuOpen(false)}
              />
              <MenuLink
                to="/library"
                label="My Library"
                icon={<Library className="h-3.5 w-3.5" />}
                onNavigate={() => setMenuOpen(false)}
              />
            </nav>
          </SheetContent>
        </Sheet>

        {/* Wordmark */}
        <Link to="/" className="flex shrink-0 items-center gap-2 text-text-bright">
          <DinoMark className="h-5 w-5 text-primary" />
          {/* On very narrow phones the glyph carries the brand alone, so the
              bar never outgrows the screen. */}
          <span className="font-mono text-[15px] font-medium lowercase tracking-tight max-[399px]:hidden">
            balasaur
          </span>
        </Link>

        {/* Primary nav — places (content spaces). Personal actions stay right.
            Hidden below 1024px: five places plus wordmark, search, and actions
            outgrow a tablet row, and forcing it made the whole PAGE scroll
            sideways. The places move to the drawer instead. */}
        <nav aria-label="Primary" className="hidden items-center gap-0.5 lg:flex">
          <NavPlace to="/" label="Browse" exact />
          <NavPlace to="/collections" label="Collections" />
          <NavPlace to="/calendar" label="Calendar" />
          <NavPlace to="/play" label="Play" />
          <NavPlace to="/night" label="Movie Night" />
        </nav>

        {/* Center search (tablet and up; phones get a full-width row below).
            min-w-0 lets it give ground first, so the bar itself can never push
            the page wider than the screen. */}
        <div className="mx-auto hidden w-full min-w-0 max-w-md md:block">
          <TopBarSearch />
        </div>

        {/* Right nav */}
        <nav className="ml-auto flex shrink-0 items-center gap-1 md:gap-2">
          <Link
            to="/watched"
            title="Swipe through titles to rate them and build your library fast"
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[5px] border border-border bg-panel px-2.5 py-1.5 text-[13px] font-bold tracking-[-0.01em] text-text-bright hover:border-primary hover:text-primary"
          >
            <Zap className="h-3.5 w-3.5" />
            Rate
          </Link>
          <Link
            to="/library"
            aria-label="My lists"
            className="hidden items-center gap-1.5 rounded-[5px] px-2 py-1.5 text-[13px] font-semibold tracking-[-0.01em] text-text-muted hover:text-text-bright sm:px-2.5 md:inline-flex"
          >
            <Library className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">My Library</span>
          </Link>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex max-w-[200px] items-center gap-2 rounded-[5px] border border-border bg-panel px-2 py-1 text-[13px] font-semibold text-text-bright hover:border-border-strong"
                >
                  {profile ? (
                    <Avatar
                      username={profile.username}
                      displayName={profile.displayName}
                      preset={profile.avatarPreset}
                      size={22}
                      className="text-[12px]"
                    />
                  ) : (
                    <span className="ph-no-capture inline-flex h-[22px] w-[22px] items-center justify-center rounded-full bg-primary text-[11px] uppercase text-primary-foreground">
                      {user.email?.[0] ?? "?"}
                    </span>
                  )}
                  <span className="ph-no-capture hidden truncate sm:inline">
                    {profile ? profile.displayName || `@${profile.username}` : user.email}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="min-w-[210px] border-border bg-panel text-[13px] text-text-bright"
              >
                {profile ? (
                  <>
                    {/* Public identity */}
                    <DropdownMenuLabel className="flex items-center gap-2 py-2">
                      <Avatar
                        username={profile.username}
                        displayName={profile.displayName}
                        preset={profile.avatarPreset}
                        size={30}
                        className="text-[12px]"
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-[12px] text-text-bright">
                          {profile.displayName || profile.username}
                        </span>
                        <span className="block truncate text-[12px] text-primary">
                          @{profile.username}
                        </span>
                      </span>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-border" />
                    <DropdownMenuItem asChild className="cursor-pointer focus:bg-background">
                      <Link to="/$handle" params={{ handle: `@${profile.username}` }}>
                        <User className="mr-2 h-3.5 w-3.5" />
                        View profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="cursor-pointer focus:bg-background">
                      <Link to="/profile">
                        <Pencil className="mr-2 h-3.5 w-3.5" />
                        Edit profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-border" />
                  </>
                ) : (
                  <>
                    <DropdownMenuLabel className="text-[12px] font-semibold text-text-dim">
                      Signed in
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-border" />
                  </>
                )}
                {/* Private settings — deliberately separate from the public profile above */}
                <DropdownMenuItem asChild className="cursor-pointer focus:bg-background">
                  <Link to="/account">
                    <Settings className="mr-2 h-3.5 w-3.5" />
                    Account settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => signOut()}
                  className="cursor-pointer focus:bg-background"
                >
                  <LogOut className="mr-2 h-3.5 w-3.5" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              type="button"
              onClick={() => setAuthOpen(true)}
              className="whitespace-nowrap rounded-[5px] bg-primary px-3 py-1.5 text-[13px] font-bold tracking-[-0.01em] text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Sign in
            </button>
          )}
        </nav>
      </div>

      {/* Mobile second row: search alone, full width. The places moved into
          the drawer, which is what lets search breathe on a phone. */}
      <div className="mx-auto max-w-grid px-4 pb-2 md:hidden">
        <TopBarSearch />
      </div>
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </header>
  );
}

// A primary-nav "place" link with an active underline. `exact` keeps Browse
// from lighting up on every route (it only owns "/").
function NavPlace({ to, label, exact = false }: { to: string; label: string; exact?: boolean }) {
  return (
    <Link
      to={to}
      activeOptions={{ exact }}
      activeProps={{ className: "text-text-bright border-primary" }}
      inactiveProps={{ className: "text-text-muted border-transparent hover:text-text-bright" }}
      className="whitespace-nowrap border-b-2 px-2 py-1 text-[13px] font-semibold tracking-[-0.01em] transition-colors"
    >
      {label}
    </Link>
  );
}

// A drawer row: same place, bigger touch target, active edge on the left.
function MenuLink({
  to,
  label,
  exact = false,
  icon,
  onNavigate,
}: {
  to: string;
  label: string;
  exact?: boolean;
  icon?: React.ReactNode;
  onNavigate: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      activeOptions={{ exact }}
      activeProps={{ className: "border-primary bg-panel text-text-bright" }}
      inactiveProps={{ className: "border-transparent text-text-muted hover:text-text-bright" }}
      className="flex items-center gap-2 border-l-2 px-4 py-2.5 text-[15px] font-bold tracking-[-0.01em] transition-colors"
    >
      {icon}
      {label}
    </Link>
  );
}
